import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bookmark, Trash2, MapPin, Search } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { API } from "../../App";

const SORT_OPTIONS = [
  { value: "recent", label: "Recently saved" },
  { value: "name_asc", label: "A → Z" },
  { value: "name_desc", label: "Z → A" },
  { value: "match_desc", label: "Best match first" },
];

const matchScore = (b) => b.recommendation?.match_score ?? -1;

export const BookmarksSection = ({ token }) => {
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent");

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/bookmarks`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setBookmarks(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const remove = async (countryName) => {
    try {
      await axios.delete(
        `${API}/bookmarks/${encodeURIComponent(countryName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookmarks((prev) => prev.filter((b) => b.country_name !== countryName));
      toast.success(`Removed ${countryName} from bookmarks`);
    } catch {
      toast.error("Failed to remove bookmark");
    }
  };

  const filteredSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? bookmarks.filter((b) => b.country_name.toLowerCase().includes(q))
      : bookmarks;

    const sorted = [...filtered];
    switch (sortBy) {
      case "name_asc":
        sorted.sort((a, b) => a.country_name.localeCompare(b.country_name));
        break;
      case "name_desc":
        sorted.sort((a, b) => b.country_name.localeCompare(a.country_name));
        break;
      case "match_desc":
        sorted.sort((a, b) => matchScore(b) - matchScore(a));
        break;
      case "recent":
      default:
        sorted.sort((a, b) =>
          (b.created_at || "").localeCompare(a.created_at || "")
        );
    }
    return sorted;
  }, [bookmarks, query, sortBy]);

  if (loading || bookmarks.length === 0) return null;

  return (
    <div className="mb-12" data-testid="bookmarks-section">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Bookmark className="h-5 w-5 text-secondary" />
          <h3 className="text-2xl font-bold text-primary">Saved Countries</h3>
          <span className="text-sm text-muted-foreground">({bookmarks.length})</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 md:items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              placeholder="Search countries…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-10 pl-9 bg-white/50 border-primary/20 focus:border-primary w-full sm:w-56"
              data-testid="bookmark-search-input"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-10 px-3 rounded-lg border border-primary/20 bg-white text-sm focus:border-primary outline-none"
            data-testid="bookmark-sort-select"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {filteredSorted.length === 0 ? (
        <p
          className="text-sm text-muted-foreground italic"
          data-testid="bookmark-empty-filter"
        >
          No saved countries match "{query}".
        </p>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          {filteredSorted.map((bookmark) => (
            <div
              key={bookmark.id}
              className="bg-card rounded-xl border border-primary/10 p-4 card-hover flex items-center justify-between gap-3"
              data-testid={`bookmark-${bookmark.country_name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <button
                onClick={() => navigate("/results")}
                className="flex items-center gap-3 text-left flex-1 min-w-0"
              >
                <MapPin className="h-5 w-5 text-secondary shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-primary truncate">
                    {bookmark.country_name}
                  </p>
                  {bookmark.recommendation?.match_score && (
                    <p className="text-xs text-muted-foreground">
                      {bookmark.recommendation.match_score}% match
                    </p>
                  )}
                </div>
              </button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => remove(bookmark.country_name)}
                aria-label={`Remove ${bookmark.country_name} from bookmarks`}
                data-testid={`remove-bookmark-${bookmark.country_name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookmarksSection;
